import { useState } from 'react';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { Typography } from '@/components/ui/typography/Typography';
import { LOGIN_PRIMARY_BUTTON } from './loginStyles';
import { EMAIL_CODE_LENGTH } from './useBackendEmailOtp';

interface EmailCodeStepProps {
  email: string;
  /** True on the right code; false keeps the step open for another try (the box clears). */
  onSubmit: (code: string) => Promise<boolean>;
  onResend: () => void;
  /** Seconds before Resend is allowed again; 0 = now. */
  resendSecondsLeft?: number;
  isSubmitting: boolean;
  onBack: () => void;
}

/**
 * "Enter verification code": six boxes (paste fills them all), Verify, Resend, and Back to the
 * email field. The app shows the same step in a bottom sheet; on the web it replaces the buttons
 * inside the panel. A full code submits by itself.
 */
export function EmailCodeStep({
  email,
  onSubmit,
  onResend,
  resendSecondsLeft = 0,
  isSubmitting,
  onBack,
}: EmailCodeStepProps) {
  const { t } = useTranslation('auth');
  const [code, setCode] = useState('');

  const submit = async (value: string) => {
    if (value.length !== EMAIL_CODE_LENGTH || isSubmitting) return;
    if (!(await onSubmit(value))) setCode('');
  };

  return (
    <form
      className='flex flex-col items-center gap-4'
      data-testid='login-email-code-step'
      onSubmit={event => {
        event.preventDefault();
        void submit(code);
      }}
    >
      <div className='text-center'>
        <Typography variant='h5' fontWeight={700}>
          {t('waasAuth.emailOtp.title')}
        </Typography>
        <Typography variant='p3' color='muted'>
          {t('waasAuth.emailOtp.subtitle', { email })}
        </Typography>
      </div>

      <InputOTP
        maxLength={EMAIL_CODE_LENGTH}
        pattern={REGEXP_ONLY_DIGITS}
        value={code}
        onChange={setCode}
        onComplete={(value: string) => void submit(value)}
        autoFocus
        disabled={isSubmitting}
        aria-label={t('waasAuth.emailOtp.codeLabel')}
        data-testid='login-email-code-input'
      >
        <InputOTPGroup>
          {Array.from({ length: EMAIL_CODE_LENGTH }, (_, index) => (
            <InputOTPSlot key={index} index={index} />
          ))}
        </InputOTPGroup>
      </InputOTP>

      <Button
        type='submit'
        className={LOGIN_PRIMARY_BUTTON}
        disabled={code.length !== EMAIL_CODE_LENGTH || isSubmitting}
        data-testid='login-email-code-submit'
      >
        {isSubmitting ? (
          <Loader2 className='h-4 w-4 animate-spin' aria-hidden />
        ) : (
          t('waasAuth.emailOtp.submit')
        )}
      </Button>

      <Button
        type='button'
        variant='link'
        disabled={resendSecondsLeft > 0 || isSubmitting}
        onClick={onResend}
        data-testid='login-email-code-resend'
      >
        {resendSecondsLeft > 0
          ? t('waasAuth.emailOtp.resendIn', { seconds: resendSecondsLeft })
          : t('waasAuth.emailOtp.resend')}
      </Button>
      <Button
        type='button'
        variant='ghost'
        size='sm'
        onClick={onBack}
        data-testid='login-email-code-back'
      >
        {t('waasAuth.emailOtp.back')}
      </Button>
    </form>
  );
}
